import React from "react";
import { getUnifiedPortalPP } from "common/helpers/url";
import styles from "../styles.scss";

type Props = {
  hostname: string;
  origin: string;
  token?: string;
};

const LegacyJsqTermsOfUse: React.FunctionComponent<Props> = ({
  hostname,
  origin,
  token = "",
}) => {
  const privacyPolicySuffixWithToken =
    token.length > 0 ? getUnifiedPortalPP(token) : "/lp/terms-and-privacy/jsq/pp";
  const privacyPolicySuffixWithoutToken =
    token.length > 0 ? getUnifiedPortalPP("") : "/lp/terms-and-privacy/jsq/pp";
  const privacyPolicyLink = `${origin}${privacyPolicySuffixWithToken}`;
  const privacyPolicyDisplay = `${origin}${privacyPolicySuffixWithoutToken}`;

  return (
    <div id="tou-content">
      <br />
      <p style={{ textAlign: "center", marginBottom: "0px" }}>
        {" "}
        <strong>Juniper Square, Inc.</strong>
      </p>
      <p style={{ textAlign: "center", marginBottom: "0px" }}>
        <strong>Terms of Use</strong>
      </p>
      <br />
      <p style={{ textAlign: "center", marginBottom: "0px" }}>
        {" "}
        <strong> Rev. January 2015</strong>
      </p>
      <br />

      <p>
        Welcome and thank you for visiting {hostname} (this &ldquo;
        <u>Site</u>&rdquo;), a website operated by Juniper Square, Inc., a Delaware corporation
        (&ldquo;<u>Juniper Square</u>&rdquo;). The Site is operated by Juniper Square on behalf
        of sponsors of investment funds that have contracted with Juniper Square (collectively,
        &ldquo;<u>Providers</u>&rdquo;). By accessing or using this Site, you as the user
        (&ldquo;<u>you</u>&rdquo;) agree to be bound by the following terms and conditions
        (these &ldquo;<u>Terms</u>&rdquo;). Please read these Terms carefully before using this
        Site. These Terms are a legally binding agreement between you and Juniper Square.
      </p>

      <p>
        BY ACCESSING OR USING THIS SITE, YOU REPRESENT THAT YOU HAVE READ, UNDERSTOOD AND AGREE
        TO THESE TERMS, INCLUDING THEIR DISCLAIMERS AND LIMITATIONS OF LIABILITY. YOU ARE GIVING
        UP SUBSTANTIAL LEGAL RIGHTS BY AGREEING TO THESE TERMS. IF YOU DO NOT AGREE TO ANY OF
        THESE TERMS, YOU MAY NOT USE THIS SITE.
      </p>

      <p>
        <strong>1. Use of this Site</strong>.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. <u>License</u>. On the condition that you comply with all of your obligations under
        these Terms, Juniper Square grants you a limited, revocable, nonexclusive,
        nonassignable, nonsublicenseable license and right to access this Site with a generally
        available web browser to view information and use this Site. Any other use of this Site
        is strictly prohibited and a violation of these Terms. Juniper Square reserves all
        rights not expressly granted in these Terms, including, without limitation, rights of
        title, ownership, intellectual property and all other rights and interests in this Site
        and all related items.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. <u>Revision of these Terms</u>. Juniper Square may revise these Terms at any time
        with or without additional notice to you. Such revised Terms will be effective when
        posted on this Site. You should revisit these Terms on a regular basis, as revised
        versions will be binding upon you. You understand and agree that your continued access
        to or use of this Site after the effective date of changes to these Terms indicates your
        acceptance of such revisions.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        c. <u>Cessation of Service</u>. Juniper Square reserves the right to modify, terminate
        and suspend the operation of this Site, as well as the provision of any or all products
        or services via this Site, without notice or liability, at any time and for any or no
        reason in its sole and absolute discretion.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        d. <u>Risks Inherent in Electronic Mail</u>. You understand that communications
        distributed by this Site may be distributed by electronic mail, which is by its nature
        insecure and unreliable. Electronic mail is subject to interception, misdirection and
        loss. In addition, an electronic mail message may be forwarded by its intended recipient
        to other unintended recipients. Any recipient of an electronic message sent by this Site
        may access the hyper-links to files contained on this Site, as this Site does not
        authenticate or validate the identity or authority of any person following a hyper-link
        contained in any such message.{" "}
      </p>

      <p>
        <strong>2. Provider Responsibility</strong>.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. <u>Information and Materials</u>. This Site enables Providers to distribute
        electronic mail and other messages (&ldquo;<u>Communications</u>&rdquo;) to you, as well
        as to collect your signatures to certain contracts, notices, disclosures or other
        records or documents to be entered into between you and a Provider (&ldquo;
        <u>Electronic Contracts</u>&rdquo;). Provider, and not Juniper Square, is solely
        responsible for the nature and content of all Communications, Electronic Contracts,
        materials, works, data, statements, and other visual, graphical, video, and written or
        audible communications provided to you via this Site. Nothing in these Terms may be
        construed to make Juniper Square a party to any Electronic Contract processed through
        this Site, and Juniper Square makes no representation or warranty regarding the
        transactions sought to be effected by any Electronic Contract. Juniper Square does not
        offer any legal or other advice on the terms, suitability or enforceability of any
        Electronic Contract hosted on this Site.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. <u>Securities, Products and Services</u>. Each Provider is solely responsible for the
        securities and any products and services that such Provider may provide to you via this
        Site. Juniper Square shall not be liable for any losses, damages or costs of any kind or
        nature arising out of or in any way connected with the use, offer, sale or provision of
        the securities, products or services offered by Provider. Juniper Square is not
        responsible for any errors or delays caused by you or Provider with respect to such
        securities, products or services.
      </p>

      <p>
        <strong>3. Your Account</strong>.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. <u>Account Creation</u>. In order to access restricted portions of this Site, you are
        required to create an account on this Site (your &ldquo;<u>Account</u>&rdquo;) and
        provide your contact information. Juniper Square reserves the right to refuse or reject
        any request to create an account for you for any or no reason at Juniper Square&rsquo;s
        sole discretion.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. <u>Responsibility</u>. You are solely responsible for your Account, your contact
        information and other information made available through your Account or otherwise via
        this Site (your &ldquo;<u>Information</u>&rdquo;). You assume all risks associated with
        Your Information, including reliance on its quality, accuracy or reliability by any
        other person or entity.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        c. <u>Termination</u>.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        {" "}
        i. <u>By You</u>. You may terminate your Account at any time by contacting your
        Provider.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        {" "}
        ii. <u>By Juniper Square</u>. If Juniper Square has reason to suspect that you have
        breached these Terms, or that any of Your Information is not authorized, correct,
        current and complete, Juniper Square may, in addition to taking or reserving any other
        remedies against you, terminate your Account. Juniper Square reserves the right to erase
        any or all of Your Information from this Site, as well as to terminate your Account
        without notice or liability, at any time and for any or no reason in its sole and
        absolute discretion. Any suspension, termination or cancellation of your Account shall
        not affect provisions of these Terms, such as indemnification and limitations of
        liability, that are by their nature intended to survive such suspension, termination or
        cancellation.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        d. <u>Prohibited Uses</u>. You agree that:
      </p>

      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        i. you will not violate these Terms or any of Juniper Square&rsquo;s rights or use this
        Site to violate the rights of any third party such as copyright or trademark rights;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        ii. you will use your true legal name and true electronic mail address, and only provide
        accurate and complete information on this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        iii. you will provide at your cost all equipment, software, mobile access and Internet
        access necessary for you to use this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        iv. you will copy information from this Site only as necessary for your personal,
        non-commercial use to view, save, print, fax or e-mail such information;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        v. you will not otherwise reproduce, modify, distribute, display or provide access to
        this Site or its content;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        vi. you will not create derivative works from, decompile, disassemble or reverse
        engineer any portion of this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        vii. you will not upload to or distribute through this Site any binary code, macros or
        other executable code, or any file that contains any viruses, Trojan horses or other
        components designed to commandeer, limit or harm the functionality of a computer;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        viii. you will not record, process or mine information about other users, or access,
        retrieve or index any portion of this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        ix. you will not remove or modify any copyright or other intellectual property notices
        that appear on this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        x. you will not access or use this Site in any manner that could damage, disable,
        overburden, place an unreasonable load on, interfere or attempt to interfere with the
        proper working of, or impair, this Site, its computer systems, network or the account of
        any other user;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        xi. you will not attempt to gain unauthorized access to any parts of this Site or any
        user accounts, or any of this Site&rsquo;s computer systems or networks;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        xii. you will not use this Site in any way that is unlawful, harms Juniper
        Square&rsquo;s business, Juniper Square&rsquo;s service providers, licensors,
        representatives or any other user, or breaches any policy or notice on this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        xiii. you will not impersonate another person or misrepresent your affiliation with
        another person or entity, such as by using another person&rsquo;s user name, password or
        other account information or another person&rsquo;s name, likeness, image or photograph;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        xiv. you will not charge any person for access to any portion of this Site or any
        information on this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        xv. you will not access this Site through automated queries (such as by screen or
        database scraping, spiders, robots, crawlers or any other automated activity with the
        purpose of obtaining information from this Site) without Juniper Square&rsquo;s prior
        express written permission;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        xvi. you will not engage in &ldquo;framing&rdquo; or &ldquo;mirroring,&rdquo; or
        otherwise simulate the appearance or functionality of this Site; and
      </p>
      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        xvii. you will not assist, encourage or enable others to do any of the preceding
        prohibited activities.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        e. <u>Juniper Square&rsquo;s Right to Use Your Information</u>. As between you and
        Juniper Square, your contact information, data and other information made available
        through your Account or that you otherwise make available from time to time on the Site
        (collectively, &ldquo;<u>Your Information</u>&rdquo;) is owned by you. You hereby
        irrevocably grant Juniper Square a worldwide, perpetual, non-exclusive, royalty-free,
        fully paid-up, assignable, sublicensable, transferable rights to use, copy, publicly
        perform and display, reproduce, distribute, modify, translate, remove, analyze,
        commercialize, and prepare derivative works of Your Information in connection with the
        Site and the services provided to you by the Site. Please note that you also irrevocably
        grant the Providers with whom you have a relationship the right to access and use Your
        Information in connection with their use of the Site.
      </p>

      <p>
        <strong>4. Representations and Warranties</strong>. You represent and warrant that:
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. you are 18 years or older and you have all requisite rights and authority to use the
        Site and to enter into these Terms;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. the performance of your obligations under these Terms will not violate, conflict
        with, or result in a default under any other agreement, including confidentiality
        agreements between you and third parties;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        c. you will use the Site and services provided by the Site for lawful purposes only and
        subject to these Terms;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        d. you are responsible for all use of the Site and services provided by the Site by
        persons accessing the Site and such services through your account;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        e. you agree that Juniper Square will not be liable for any losses incurred as a result
        of a third party&rsquo;s use of the Site and services provided by the Site associated
        with your account, regardless of whether such use is with or without your knowledge and
        consent;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        f. you will not use the Site and services provided by the Site in any manner that could
        damage, disable, overburden or impair the Site, or interfere with use of the Site or
        services by others;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        g. any information submitted to the Site or Juniper Square by you is true, accurate, and
        correct;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        h. you understand that, except at expressly stated in these Terms, these Terms do not
        grant you any license to use, reproduce, distribute, display or provide access to any
        portion of this Site on third-party websites, or otherwise;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        i. you will not attempt to gain unauthorized access to the Site or services provided by
        the Site, other accounts, computer systems, or networks under the control or
        responsibility of Juniper Square through hacking, cracking, password mining, or any
        other unauthorized means;{" "}
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        j. you agree to immediately notify Juniper Square of any unauthorized use of your
        account of which you become aware;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        k. you are solely responsible for maintaining the confidentiality of your account names
        and password(s);
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        l. you are not a competitor of Juniper Square and are not using the Site or services
        provided by the Site for reasons that are in competition with Juniper Square;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        m. if you use this Site in your capacity as an employee, owner or otherwise as an agent
        of another person or entity, you agree on behalf of yourself and such person or entity,
        jointly and severally, to be bound by these Terms;
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        n. you will not impersonate another person or misrepresent your affiliation with another
        person or entity, including using another person&rsquo;s username, password or other
        account information or another person&rsquo;s name, likeness, image or photograph; and
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        o. your consent to be bound to any Electronic Contract, whether by typing your name,
        checking a box, pressing a button, clicking through a link, or demonstrating other
        intent to be bound to such Electronic Contract, shall create a legal, valid and binding
        contract enforceable against you in accordance with its terms.
      </p>

      <p>
        <strong>5. Intellectual Property</strong>.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. <u>Content of this Site</u>. Juniper Square owns all of the content that is made
        available in connection with this Site (the &ldquo;<u>Site Content</u>&rdquo;),
        including visual interfaces, interactive features, graphics, designs, databases and
        their data, computer code, products, software and all other elements and components of
        this Site. Juniper Square also owns the copyrights, trademarks, service marks, trade
        names and other intellectual and proprietary rights throughout the world associated with
        this Site and the Site Content, which are protected by copyright, trade dress, patent,
        trademark and other applicable intellectual property rights and laws. Notwithstanding
        the foregoing, Juniper Square does not assert any ownership over the proprietary
        information or registered intellectual property rights of any Provider, and Juniper
        Square agrees that data and information provided by you under these Terms shall remain,
        as between you and Juniper Square, owned by you.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. <u>Restrictions</u>. You may not modify, reproduce, distribute, create derivative
        works or adaptations of, publicly display, perform or in any way exploit any of the Site
        Content in whole or in part, except as expressly authorized by the applicable rights
        holder. Except as expressly and unambiguously provided by these Terms, Juniper Square
        does not transfer to you any of Juniper Square&rsquo;s technology or other intellectual
        property or technology rights. All right, title and interest in and to Juniper
        Square&rsquo;s technology and intellectual property will remain solely with Juniper
        Square.
      </p>

      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        c. <u>Feedback</u>. By providing ideas, suggestions, documents or proposals (&ldquo;
        <u>Feedback</u>&rdquo;) concerning this Site, you represent, warrant and agree that:
      </p>

      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        {" "}
        i. your Feedback does not contain the confidential proprietary information of third
        parties,
      </p>

      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        ii. neither Provider nor Juniper Square are under any obligation of confidentiality,
        express or implied, with respect to the Feedback; and
      </p>

      <p className={styles.TermsAndPolicy__legalSubsubsection}>
        iii. Juniper Square may have something similar to the Feedback already under
        consideration or in development.
      </p>

      <p>
        {" "}
        You hereby grant Juniper Square a worldwide, perpetual, non-exclusive, royalty-free,
        fully paid-up, irrevocable, assignable, sublicensable, transferable license to use,
        modify, prepare derivative works of, publish, distribute and sublicense all Feedback,
        and you irrevocably waive, and cause to be waived, against Juniper Square, Provider and
        the users of this Site, all claims and assertions of any moral rights contained in such
        Feedback.
      </p>

      <p>
        <strong>6. Privacy</strong>. Juniper Square respects your privacy and limits the sharing
        of data about you with third parties. Please consult Juniper Square&rsquo;s Privacy
        Policy, available at{" "}
        <a href={privacyPolicyLink} target="_blank" rel="noreferrer" data-testid="pplink">
          {privacyPolicyDisplay}
        </a>
        , for more information. By agreeing to these Terms, you acknowledge and consent to the
        use, sharing and disclosure of your personal information and data as described in such
        Juniper Square Privacy Policy, as it may be amended from time to time.
      </p>
      <p>
        <strong>7. Indemnification</strong>. You agree to indemnify, defend and hold Juniper
        Square, as well as Juniper Square&rsquo;s parents, subsidiaries, affiliates, any related
        companies, suppliers, licensors and partners, including the shareholders, officers,
        directors, employees, agents and representatives of each of them (collectively, the
        &ldquo;<u>Juniper Square Parties</u>&rdquo;) harmless from any and all claims,
        liability, damages and costs (including, but not limited to, attorneys&rsquo; fees)
        arising out of or relating to:
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. your access to or use of this Site;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. your violation of these Terms, including without limitation your breach of any
        representation or warranty;
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        c. your infringement, or the infringement of any third party using your Account, of any
        intellectual property or other right of any person or entity; or
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        d. the nature and content of all materials, Electronic Contracts, works, data,
        statements and other visual, graphical, written or audible communications of any nature
        submitted by you or otherwise processed through your Account.
      </p>
      <p>
        Juniper Square reserves the right to, but is not obligated to, assume the exclusive
        defense and control of any matter for which you are required to indemnify the Juniper
        Square Parties, and you agree to cooperate with Juniper Square&rsquo;s defense of such
        claims. You agree not to settle any such matter without Juniper Square&rsquo;s prior
        written consent. Juniper Square will use reasonable efforts to notify you of any such
        claim, action or proceeding when Juniper Square becomes aware of it. Each of the Juniper
        Square Parties is an express third party beneficiary of this indemnification provision,
        with full rights to enforce its terms.
      </p>
      <p>
        <strong>8. Third Party Services</strong>. This Site includes links to third-party
        products, services and websites, as well as materials provided by third parties
        (collectively, &ldquo;<u>Third-Party Content</u>&rdquo;). Neither these Terms nor this
        Site endorse or take responsibility for any Third-Party Content. You agree that The
        Juniper Square Parties are not responsible for the availability or contents of
        Third-Party Content. You understand that The Juniper Square Parties have no obligation
        to, and generally do not, approve or monitor materials provided by third parties through
        this Site. Your use of Third-Party Content is at your own risk.
      </p>
      <p>
        <strong>9. Limitations of Liability</strong>.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. <u>No Consequential Damages</u>. Juniper Square shall not be liable for any indirect,
        special, incidental, exemplary, punitive or consequential loss or damage of any kind,
        including without limitation, lost business or profits, whether arising in tort
        (including negligence), equity, breach of contract or otherwise (and whether or not such
        damages are foreseeable or Juniper Square has been advised of the possibility of such
        loss or damage). In no event will any of the officers, trustees, directors, partners,
        employees, consultants, beneficiaries, joint venturers, members, stockholders or other
        principals or representatives of Juniper Square, ever be personally liable to you under,
        arising out of, or related to these Terms (including for direct or consequential
        damages), and you hereby waive the right to recover damages from any such persons.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. <u>LIMITATION OF LIABILITY</u>. NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED IN
        THIS AGREEMENT, THE JUNIPER SQUARE PARTIES&rsquo; MAXIMUM AGGREGATE CUMULATIVE LIABILITY
        ARISING OUT OF OR RELATED TO THESE TERMS AND THE SITE SHALL NOT EXCEED ONE HUNDRED
        DOLLARS ($100.00). THE ABOVE LIMITATION WILL APPLY WHETHER AN ACTION IS IN CONTRACT,
        EQUITY, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE, AND REGARDLESS OF THE THEORY OF
        LIABILITY. If applicable law limits the application of the above limitation of
        liability, Juniper Square&rsquo;s liability will be limited to the maximum extent
        permitted by law.
      </p>
      <p>
        <strong>10. DISCLAIMER OF WARRANTIES</strong>. THIS SITE IS PROVIDED &ldquo;AS
        IS,&rdquo; AND JUNIPER SQUARE: (i) MAKES NO REPRESENTATION OR WARRANTY OF ANY KIND
        WHETHER EXPRESS, IMPLIED (EITHER IN FACT OR BY OPERATION OF LAW), OR STATUTORY, AS TO
        ANY MATTER WHATSOEVER; (ii) EXPRESSLY DISCLAIMS ALL IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, QUALITY, ACCURACY, AND TITLE; AND
        (iii) DOES NOT WARRANT THAT THIS SITE IS OR WILL BE ERROR-FREE, WILL MEET YOUR
        REQUIREMENTS, OR BE TIMELY OR SECURE. YOU WILL BE SOLELY RESPONSIBLE FOR ANY DAMAGE
        RESULTING FROM THE USE OF THIS SITE. YOU WILL NOT HAVE THE RIGHT TO MAKE OR PASS ON ANY
        REPRESENTATION OR WARRANTY ON BEHALF OF JUNIPER SQUARE TO ANY THIRD PARTY. USE OF THIS
        SITE IS AT YOUR SOLE RISK. JUNIPER SQUARE MAKES NO GUARANTY OF CONFIDENTIALITY OR
        PRIVACY OF ANY COMMUNICATION OR INFORMATION TRANSMITTED ON THIS SITE. Because some
        states and jurisdictions do not allow limitations on implied warranties, the above
        limitation may not apply to you. In such event, such warranties are limited to the
        minimum warranty period allowed by the applicable law. Without limiting the generality
        of the foregoing, Juniper Square does not make any warranty of any kind with respect to
        freedom from patent, trademark, or copyright infringement, or theft of trade secrets and
        Juniper Square does not assume any liability hereunder for any infringement of any
        patent, trademark, or copyright arising from the use of the Site or rights granted or
        provided by Juniper Square hereunder.
      </p>
      <p>
        <strong>11. Confidentiality</strong>. The information available on the Site relates to
        Juniper Square as well as Providers, their respective affiliates and affiliated
        investment funds (collectively, the &ldquo;<u>Disclosing Parties</u>&rdquo;). This
        information is highly confidential. You hereby agree to maintain such information in
        strict confidence and acknowledge that such information is proprietary and confidential,
        that the Disclosing Parties and their affiliates derive independent economic value from
        such information not being generally known, and that such information is subject to
        reasonable efforts by the Disclosing Parties to maintain its secrecy. You hereby agree
        that information available on the Site constitutes trade secrets and that the disclosure
        of any such information will cause substantial and irreparable competitive harm, as well
        as financial and strategic damage, to the Disclosing Parties, their affiliates and/or
        the investment funds and opportunities managed by the Disclosing Parties or their
        affiliates (each a &ldquo;<u>Fund</u>&rdquo;). The reports available on the Site are for
        use by investors and potential investors or their authorized designees in such a Fund,
        and you hereby agree that this information shall only be used by you in connection with
        an evaluation or the monitoring of your investment in such Fund. In addition, you agree
        that any information on this Site shall not be disclosed to any other person or entity
        or reproduced in any manner (by print, by electronic means or otherwise) without the
        express written authorization of the applicable Disclosing Party; provided, that such
        information may be disclosed on a need-to-know basis to other employees of your
        organization and to your investment advisers (so long as (i) they are bound by an
        obligation to maintain the confidentiality of such information that is at least as
        protective as your obligations under these Terms and Conditions, and (ii) such other
        employees and investment advisers are not given your password to access this Site) or as
        explicitly required by applicable law. You agree to be responsible for the consequences
        of dissemination of any information from the Site by such employees and investment
        advisers.
      </p>
      <p>
        <strong>12. Miscellaneous</strong>.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        a. <u>Governing Law</u>. These Terms shall be governed by the laws of the State of
        California, regardless of conflict of laws principles.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        b. <u>Arbitration</u>. Any dispute, controversy or claim arising out of or in connection
        with or relating to your use of this Site or these Terms, or any breach or alleged
        breach these Terms, shall be submitted to a single arbitrator and settled by binding
        arbitration pursuant to the Commercial Rules then in effect of the American Arbitration
        Association. Any such controversy or claim shall be arbitrated on an individual basis,
        and shall not be consolidated in any arbitration with any claim or controversy of any
        other party. The arbitration shall be conducted in the City and County of San Francisco,
        California. Judgment upon the award may be entered in any court of competent
        jurisdiction. EACH PARTY HEREBY KNOWINGLY, VOLUNTARILY AND INTENTIONALLY WAIVES ANY
        RIGHT IT MAY HAVE TO A TRIAL BY JURY IN RESPECT OF ANY DISPUTE ARISING OUT OF, UNDER OR
        IN CONNECTION WITH THESE TERMS.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        c. <u>Export Laws</u>. You acknowledge that technology utilized by this Site and any
        related products, information, documentation, software, technology, technical data, and
        any derivatives thereof, that Juniper Square makes available to its users (collectively
        &ldquo;<u>Excluded Data</u>&rdquo;), may be subject to export control laws and
        regulations of the United States and other jurisdictions (collectively &ldquo;
        <u>Export Laws</u>&rdquo;). You represent and warrant that: (i) you are not located in,
        under the control of, or a national or resident of an embargoed country or prohibited
        end user under Export Laws; and (ii) you will not access, download, use, export or
        re-export, directly or indirectly, the Excluded Data to any location, entity, government
        or person prohibited by export laws, without first complying with all Export Laws that
        may be imposed by the U.S. Government and any country or organization of nations within
        whose jurisdiction it operates or does business. You are solely responsible for
        complying with Export Laws for all Excluded Data and any of its content transmitted
        through the Site and Services. You shall advise Juniper Square in the event the Excluded
        Data requires Juniper Square to obtain additional licenses, permits and/or approvals
        from any government in the jurisdiction where you intend to use the Site or Services.
        Upon being advised of such a requirement, Juniper Square may at its sole discretion: (a)
        terminate your Account; (b) obtain such licenses, permits, and/or approvals as may be
        required; or (c) modify these Terms such that additional licenses, permits, and/or
        approvals are no longer required to be obtained by Juniper Square.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        d. <u>No Assignment</u>. These Terms, and any rights or obligations under these Terms,
        are not assignable, transferable or sublicensable by you except with Juniper
        Square&rsquo;s prior written consent, but may be assigned or transferred by Juniper
        Square without restriction. Any attempted assignment by you shall violate these Terms
        and be void. Juniper Square may assign these Terms of Use without your consent.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        e. <u>Beneficiaries</u>. Except as otherwise expressly stated in these Terms, nothing in
        these Terms is intended to, nor will be deemed to, confer rights or remedies upon any
        third party.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        f. <u>Amendments; Waivers</u>. Juniper Square may modify these Terms as provided in
        Section 1(b) (Revision of these Terms). These Terms may not be modified, amended,
        terminated or waived, in whole or in part, by you except by a written instrument signed
        by Juniper Square. Except as expressly set forth herein, any failure of a party to take
        action in response to any breach of these Terms by the other party shall not constitute
        a waiver of such breach or of performance required by the other party; and no waiver of
        any provision of these Terms shall constitute a waiver of any other provision (whether
        or not similar), nor shall such waiver constitute a continuing waiver unless expressly
        provided therein.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        g. <u>Force Majeure</u>. Neither party will be liable for, or be considered to be in
        breach of or default under these Terms on account of any delay or failure to perform as
        required by these Terms as a result of any cause or condition beyond such party&rsquo;s
        reasonable control, so long as such party uses all commercially reasonable efforts to
        avoid or remove such causes of non-performance or delay.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        h. <u>Notices</u>. To send notices to Juniper Square, or if you have any questions
        regarding these Terms, please contact Juniper Square at legal@junipersquare.com. Juniper
        Square may provide you with notices, including notices of changes to these Terms, by
        electronic mail, regular mail or communications through this Site.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        i. <u>Entire Agreement</u>. These Terms contain the entire agreement between the parties
        with respect to the subject matter hereof, and supersedes all prior and contemporaneous
        representations, promises, agreements and understandings, whether oral or written,
        between the parties concerning the subject matter hereof. You may from time to time
        enter into binding legal agreements relating to certain services available through the
        Site, which may have terms that are different from those of these Terms. In the event of
        any inconsistency, the terms of such other agreement shall control with respect to such
        services.
      </p>
      <p className={styles.TermsAndPolicy__legalSubsection}>
        {" "}
        j. <u>Severability</u>. If any provision of these Terms is deemed to be invalid or
        unenforceable, the remaining provisions of these Terms shall be valid and binding and of
        like effect as though such provision were not included.
      </p>
    </div>
  );
};

export default LegacyJsqTermsOfUse;
